import clsx from "clsx";
import { isEmpty } from "lodash";
import React, { useState, useRef, useEffect } from "react";
import { toastr } from "react-redux-toastr";
import { Typography, Box, Grid, Button, useMediaQuery, useTheme } from "@material-ui/core";
// component
import { InputField } from "components";
import { getCurrency } from "helper/numberFormat";
// direct import
import "../style.scss";
import { MyCheckboxRenewal } from "../checkbox";
import { Fonts } from "theme/fontConfig";

const AddOns = ({ values, selectedValue, setValue }) => {
  const currency = getCurrency();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const [isAddingOther, setIsAddingOther] = useState(false);
  const [otherAmount, setOtherAmount] = useState("");
  const inputWrapperRef = useRef(null);

  const getAddOns = values
    ?.filter((amount) => amount?.amount !== 0 && !amount?.openAmount)
    ?.map((el) => ({
      ...el,
      label: el?.title,
      amount: Number(el?.amount),
    }));

  const isOpenAmount = values?.find((el) => el?.openAmount === true);
  const hasVisibleAddOns = !isEmpty(getAddOns) || !!isOpenAmount;

  //  check if already added
  const isOtherAmountAdded =
    values?.find((el) => el?.openAmountAdded === true) ?? false;

  const isSelected = (option) => selectedValue?.includes(option?._id) ?? false;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSaveOther = () => {
    const isAmountAlready = values?.some(
      (el) => el?.amount === Number(otherAmount)
    );
    if (isAmountAlready) {
      toastr.error("Error", "Amount already added in add-on");
      return;
    }
    if (!otherAmount || isNaN(otherAmount)) return;
    const getOpenAmount = values?.find((el) => el?.openAmount);
    const getNotAmount = values?.filter((el) => !el?.openAmount);

    const newOption = {
      ...getOpenAmount,
      amount: Number(otherAmount),
      openAmountAdded: true,
    };

    const updatedList = [...getNotAmount, newOption];
    setValue("formInfo.addOns", updatedList);
    setTimeout(() => {
      setValue("addonsOptions", [...selectedValue, getOpenAmount?._id]);
    }, 200);
    setOtherAmount("");
  };

  useEffect(() => {
    if (values?.length > 0) {
      const isRequired = values
        ?.filter((el) => el?.isRequired)
        ?.map((el) => el?._id);
      if (isRequired) {
        setValue(
          "addonsOptions",
          Array.from(new Set([...selectedValue, ...isRequired]))
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  // Handle outside click to save
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        inputWrapperRef.current &&
        !inputWrapperRef.current.contains(event.target) &&
        otherAmount &&
        !isNaN(otherAmount)
      ) {
        handleSaveOther();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [otherAmount, handleSaveOther]);

  const handleCheck = (e, id) => {
    if (e.target.checked) {
      setValue("addonsOptions", [...selectedValue, id]);
    } else {
      setValue(
        "addonsOptions",
        selectedValue?.filter((el) => el !== id)
      );
    }
  };

  return (
    <Box className="summary-renewal__card">
      <Typography className="summary-renewal__title">Add-ons</Typography>
      <Grid container spacing={2}>
        {getAddOns?.map((option, index) => {
          const selected = isSelected(option);
          return (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Button
                fullWidth
                variant="outlined"
                style={{ height: 49 }}
                className={clsx(
                  "summary-renewal__option-button",
                  (selected || option?.isRequired) && "selected"
                )}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  width="100%"
                >
                  <MyCheckboxRenewal
                    color="primary"
                    checked={selected || option?.isRequired}
                    onChange={(e) => handleCheck(e, option._id)}
                    style={{ margin: 0, marginRight: 10 }}
                    name={`${option._id}`}
                    disabled={option?.isRequired}
                  />

                  <Typography
                    style={{
                      color: (option?.isRequired || selected) ? "#155eef" : "#1e293b",
                      ...Fonts.GeneralSubHeaderFont,
                    }}
                  >
                    {`${option.label}:`} {currency}
                    {Number(option.amount).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Box>
              </Button>
            </Grid>
          );
        })}

        {isAddingOther && !isOtherAmountAdded && (
          <Grid item xs={12} sm={4}>
            <Box
              ref={inputWrapperRef}
              display="flex"
              alignItems="center"
              sx={{ position: "relative", width: "100%" }}
            >
              <InputField
                type="number"
                placeholder={`Enter ${isOpenAmount?.title} (open amount)`}
                inputClassName="account__input"
                value={otherAmount}
                handleChange={(e) => setOtherAmount(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSaveOther();
                  }
                }}
                title="none"
                formStyle={{ width: "100%", marginBottom: 0 }}
              />
            </Box>
          </Grid>
        )}

        {isOpenAmount && (
          <Grid item xs={12}>
            <Box style={{ paddingLeft: 15 }}>
              <MyCheckboxRenewal
                color="primary"
                checked={isAddingOther}
                onChange={(e) => {
                  if (!e.target.checked) {
                    const openAmountItem = values?.find((el) => el?.openAmount);
                    const updatedValues = values?.map((el) => {
                      if (el?._id === openAmountItem?._id) {
                        return {
                          ...el,
                          amount: 0,
                          openAmountAdded: false,
                        };
                      }
                      return el;
                    });
                    const remove = selectedValue?.filter(
                      (item) => item !== openAmountItem?._id
                    );
                    setValue("formInfo.addOns", updatedValues);
                    setValue("addonsOptions", remove);
                    setOtherAmount("");
                    setIsAddingOther(e.target.checked);
                  } else {
                    setIsAddingOther(e.target.checked);
                  }
                }}
                title={`${isOpenAmount?.title} (open amount)`}
                name="otherAmount"
              />
            </Box>
          </Grid>
        )}
      </Grid>

      {!hasVisibleAddOns && (
        <Box
          sx={{
            flexDirection: "column",
            height: 100,
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Typography variant="h6">No data available.</Typography>
        </Box>
      )}

    </Box>
  );
};

export default AddOns;
